import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { FaSpinner, FaEdit, FaTrash, FaTrashAlt, FaUsers, FaPlus, FaUserPlus, FaSearch } from 'react-icons/fa';
import { Modal, Button, Form, Alert, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import DataTable from 'react-data-table-component';
import SpinnerLoader from '../../components/Buttons/SpinnerLoader';
import './Groups.css';
import { groupApi, guestApi } from '../../api/guestList';
import { useAuthContext } from '../../context/AuthContext';

function Groups() {
  const { user, events, currentEvent, isAuthenticated } = useAuthContext();

  // State for groups
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [availableGuests, setAvailableGuests] = useState([]);

  // Form states
  const [groupForm, setGroupForm] = useState({
    group_name: '',
    description: ''
  });
  const [selectedGuestId, setSelectedGuestId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch groups on component mount
  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      fetchGroups();
      fetchAvailableGuests();
    }
  }, [isAuthenticated, currentEvent]);

  const fetchGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await groupApi.getGroups();
      
      // The API returns nested data: { data: { data: [...] } }
      let groupsData = response?.data?.data?.data || response?.data?.data || response?.data || response || [];

      if (!Array.isArray(groupsData)) {
        groupsData = groupsData?.groups || groupsData?.data || [];
      }

      setGroups(Array.isArray(groupsData) ? groupsData : []);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Failed to load groups. Please refresh the page.');
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableGuests = async () => {
    try {
      const response = await guestApi.getGuests();
      let guestsData = response?.data || response || [];
      
      if (!Array.isArray(guestsData)) {
        guestsData = guestsData?.guests || guestsData?.data || [];
      }
      
      setAvailableGuests(Array.isArray(guestsData) ? guestsData : []);
    } catch (err) {
      console.error('Error fetching guests:', err);
      setAvailableGuests([]);
    }
  };

  const fetchGroupMembers = async (groupId) => {
    try {
      const response = await groupApi.getGroup(groupId);
      const groupData = response?.data?.group || response?.data?.data || response?.data || response || {};
      let members = groupData.guests || groupData.members || [];

      if (!Array.isArray(members)) {
        members = Object.values(members || {});
      }
      setGroupMembers(Array.isArray(members) ? members : []);
    } catch (err) {
      console.error('Error fetching group members:', err);
      toast.error('Failed to load group members');
    }
  };

  // Create new group
  const handleCreateGroup = async () => {
    if (!groupForm.group_name.trim()) {
      toast.warning('Please enter group name');
      return;
    }

    setSubmitting(true);
    try {
      const groupData = {
        group_name: groupForm.group_name.trim(),
        description: groupForm.description || null,
        event_id: currentEvent?.id
      };

      const response = await groupApi.createGroup(groupData);

      handleApiResponse(response);
      setShowModal(false);
      resetGroupForm();
      fetchGroups();

    } catch (err) {
      console.error('Error creating group:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Update group
  const handleUpdateGroup = async () => {
    if (!selectedGroup) return;

    setSubmitting(true);
    try {
      const response = await groupApi.updateGroup(selectedGroup.id, {
        group_name: selectedGroup.group_name,
        description: selectedGroup.description
      });

      handleApiResponse(response);
      setShowEditModal(false);
      setSelectedGroup(null);
      fetchGroups();

    } catch (err) {
      console.error('Error updating group:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete group (soft delete)
  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;

    setSubmitting(true);
    try {
      const response = await groupApi.deleteGroup(selectedGroup.id);

      handleApiResponse(response);
      setShowDeleteModal(false);
      setSelectedGroup(null);
      fetchGroups();

    } catch (err) {
      console.error('Error deleting group:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Add member to group
  const handleAddMember = async () => {
    if (!selectedGroup || !selectedGuestId) {
      toast.warning('Please select a guest to add');
      return;
    }

    setSubmitting(true);
    try {
      await groupApi.addMember({
        group_id: selectedGroup.id,
        guest_ids: [selectedGuestId],
        // Fallbacks for variable backend expectations
        guest_list_group_id: selectedGroup.id,
        guest_list_ids: [selectedGuestId],
        guest_id: selectedGuestId,
        event_id: currentEvent?.id
      });

      toast.success('Member added to group successfully!');
      setShowAddMemberModal(false);
      setSelectedGuestId('');
      fetchGroupMembers(selectedGroup.id);
      fetchAvailableGuests();

    } catch (err) {
      console.error('Error adding member:', err);
      toast.error(err.message || 'Failed to add member');
    } finally {
      setSubmitting(false);
    }
  };

  // Remove member from group
  const handleRemoveMember = async (memberId) => {
    if (!selectedGroup) return;

    try {
      await groupApi.removeMember({
        group_id: selectedGroup.id,
        guest_ids: [memberId],
        // Fallbacks for variable backend expectations
        guest_list_group_id: selectedGroup.id,
        guest_list_ids: [memberId],
        member_id: memberId,
        guest_id: memberId
      });

      toast.success('Member removed from group');
      fetchGroupMembers(selectedGroup.id);
      fetchAvailableGuests();

    } catch (err) {
      console.error('Error removing member:', err);
      toast.error(err.message || 'Failed to remove member');
    }
  };

  // View group members
  const handleViewMembers = async (group) => {
    setSelectedGroup(group);
    await fetchGroupMembers(group.id);
    setShowMembersModal(true);
  };

  // Open edit modal
  const handleEditGroup = async (group) => {
    setSelectedGroup(group);
    setShowEditModal(true);
  };

  // Open delete modal
  const handleDeleteConfirm = (group) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  // Open add member modal
  const handleOpenAddMember = async (group) => {
    setSelectedGroup(group);
    setSelectedGuestId('');
    setShowAddMemberModal(true);
  };

  const resetGroupForm = () => {
    setGroupForm({
      group_name: '',
      description: ''
    });
  };

  // Filter groups based on search
  const filteredGroups = groups.filter(group =>
    group.group_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );


  if (loading) {
    return <SpinnerLoader />;
  }

  if (error) {
    return (
      <div className="container mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Groups</Alert.Heading>
          <p>{error}</p>
          <Button onClick={() => fetchGroups()}>Retry</Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">

      {/* Control Buttons & Search Toolbar */}
      <div className="bg-white p-3 rounded-4 shadow-sm border mb-4 d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-3">
        <div className="position-relative flex-grow-1" style={{ maxWidth: '420px' }}>
          <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" style={{ zIndex: 5 }} />
          <Form.Control
            type="text"
            placeholder="Search groups by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ps-5 shadow-none border"
            style={{ borderRadius: '25px', height: '42px', fontSize: '0.85rem', borderColor: '#e2e8f0' }}
          />
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap flex-sm-nowrap">
          <Button
            variant="primary"
            onClick={() => setShowModal(true)}
            className="rounded-pill px-3 px-md-4 py-2 fw-semibold shadow-sm border-0 d-flex align-items-center justify-content-center gap-2"
            style={{ backgroundColor: '#4169e1', height: '42px', fontSize: '0.85rem' }}
          >
            <FaPlus size={14} />
            Ongeza Kikundi
          </Button>
        </div>
      </div>

      {/* Group Cards Grid */}
      <div className="row g-4 mb-5">
        {filteredGroups.length > 0 ? (
          filteredGroups.map((group) => (
            <div key={group.id} className="col-12 col-md-6 col-xl-4">
              <div 
                className="card h-100 shadow-sm border-0 group-card" 
                style={{ borderRadius: '12px', cursor: 'pointer', transition: 'transform 0.2s' }}
                onClick={() => handleViewMembers(group)}
              >
                <div className="card-body d-flex flex-column p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="card-title fw-bold mb-0 text-dark d-flex align-items-center">
                      <FaUsers className="me-2 text-primary" /> 
                      {group.group_name}
                      {(group.member_count !== undefined || group.guests?.length !== undefined) && (
                        <span className="badge bg-primary ms-2 rounded-pill" style={{ fontSize: '0.8rem' }}>
                          {group.member_count ?? group.guests?.length ?? 0}
                        </span>
                      )}
                    </h5>
                  </div>
                  <p className="card-text text-muted small flex-grow-1 mb-4" style={{ minHeight: '40px' }}>
                    {group.description || 'Hakuna maelezo'}
                  </p>
                  
                  <div className="d-flex justify-content-between align-items-center pt-3 border-top mt-auto" onClick={e => e.stopPropagation()}>
                    <Button variant="light" className="text-primary rounded-circle shadow-sm" style={{ width: '40px', height: '40px', padding: 0 }} onClick={() => handleViewMembers(group)} title="View Members">
                      <FaUsers />
                    </Button>
                    <Button variant="light" className="text-success rounded-circle shadow-sm" style={{ width: '40px', height: '40px', padding: 0 }} onClick={() => handleOpenAddMember(group)} title="Add Member">
                      <FaUserPlus />
                    </Button>
                    <Button variant="light" className="text-warning rounded-circle shadow-sm" style={{ width: '40px', height: '40px', padding: 0 }} onClick={() => handleEditGroup(group)} title="Edit Group">
                      <FaEdit />
                    </Button>
                    <Button variant="light" className="text-danger rounded-circle shadow-sm" style={{ width: '40px', height: '40px', padding: 0 }} onClick={() => handleDeleteConfirm(group)} title="Delete Group">
                      <FaTrashAlt />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-12 text-center py-5">
            <div className="text-muted mb-3"><FaUsers size={48} opacity={0.5} /></div>
            <h5 className="text-muted">Hakuna makundi yaliyopatikana.</h5>
            <p className="text-muted small">Click "Ongeza Kikundi" to create a new group.</p>
          </div>
        )}
      </div>

      {/* Create Group Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Ongeza Kikundi Kipya</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Jina la Kikundi *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter group name"
                value={groupForm.group_name}
                onChange={(e) => setGroupForm({ ...groupForm, group_name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Maelezo (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter group description"
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateGroup} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : 'Create Group'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Group Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Hariri Kikundi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedGroup && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Jina la Kikundi *</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedGroup.group_name}
                  onChange={(e) => setSelectedGroup({ ...selectedGroup, group_name: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Maelezo</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={selectedGroup.description || ''}
                  onChange={(e) => setSelectedGroup({ ...selectedGroup, description: e.target.value })}
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateGroup} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : 'Update Group'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Group Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Futa Kikundi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedGroup && (
            <div>
              <p>Are you sure you want to delete this group?</p>
              <div className="card p-3 bg-light">
                <strong>Group Name:</strong> {selectedGroup.group_name}<br />
                <strong>Description:</strong> {selectedGroup.description || '-'}
              </div>
              <p className="text-danger mt-3">
                Warning: This will remove all members from this group. The guests will not be deleted, only removed from this group.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteGroup} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : 'Delete Group'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Members Modal */}
      <Modal show={showMembersModal} onHide={() => setShowMembersModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUsers className="me-2" />
            Members of {selectedGroup?.group_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {groupMembers.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Phone Number</th>
                  <th>Email</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {groupMembers.map((member, index) => (
                  <tr key={member.id}>
                    <td>{index + 1}</td>
                    <td>{member.name || member.full_name}</td>
                    <td>{member.phone_number || member.mobile}</td>
                    <td>{member.email || '-'}</td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemoveMember(member.id)}
                      >
                        <FaTrash /> Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5">
              <p>No members in this group yet.</p>
              <Button
                variant="primary"
                onClick={() => {
                  setShowMembersModal(false);
                  handleOpenAddMember(selectedGroup);
                }}
              >
                <FaUserPlus className="me-2" />
                Add Members
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMembersModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowMembersModal(false);
              handleOpenAddMember(selectedGroup);
            }}
          >
            <FaUserPlus className="me-2" />
            Add Member
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Member Modal */}
      <Modal show={showAddMemberModal} onHide={() => setShowAddMemberModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUserPlus className="me-2" />
            Add Member to {selectedGroup?.group_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select Guest</Form.Label>
              <Form.Select
                value={selectedGuestId}
                onChange={(e) => setSelectedGuestId(e.target.value)}
              >
                <option value="">Choose a guest...</option>
                {Array.isArray(availableGuests) && availableGuests.map(guest => (
                  <option key={guest.id} value={guest.id}>
                    {guest.name || guest.full_name} - {guest.phone_number || guest.mobile}
                  </option>
                ))}
              </Form.Select>
              {(!availableGuests || availableGuests.length === 0) && (
                <Form.Text className="text-muted">
                  No guests available. Please add guests first in the "Wageni" section.
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddMemberModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddMember}
            disabled={submitting || !selectedGuestId}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : 'Add Member'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Groups;